import { UsageService } from '../../../core/usage/usage.service';
import { PatientAiService } from 'src/app/core/services/patient-ai.service';
import { SpeedTestService } from './../../../core/services/speed-test.service';
import { NetworkConnectivityService } from './../../../core/services/network-connectivity.service';
import { MediaService } from './../../../core/services/media.service';
import { UserService } from './../../../core/services/user.service';
import { PatientService } from './../../../core/services/patient.service';
import { PatientAppointmentService } from './../../../core/services/patient-appointment.service';
import { PatientNameParts, patientFullName } from './../../pipes/patient-heading.pipe';
import { isPlatformBrowser } from '@angular/common';
import { Component, HostListener, Inject, NgZone, OnDestroy, OnInit, Optional, PLATFORM_ID, inject } from '@angular/core';
import { OverlayContainer } from '@angular/cdk/overlay';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import * as moment from 'moment';
import { Subject, Subscription, filter, interval, switchMap, takeUntil } from 'rxjs';
import { AIService } from '../../../core/services/ai.service';
import { FileObject } from './../../../core/interfaces/file-object';
import { AnalyticsService } from './../../../core/services/analytics.service';
import { AudioService } from './../../../core/services/audio.service';
import { FeedbackService } from './../../../core/services/feedback.service';
import { LoggerService } from './../../../core/services/logger.service';
import { NoteTypeService } from './../../../core/services/note-type.service';
import { PatientNoteService } from './../../../core/services/patient-note.service';
import { TokenUsageService } from './../../../core/services/token-usage.service';
import { RecordingAlertService } from './../../../core/services/recording-alert.service';
import { RecordingStorageService } from './../../../core/services/recording-storage.service';
import { GroupService } from './../../../core/services/group.service';
import { AudioSegment as StorageAudioSegment, FailedRecording } from './../../../core/interfaces/recording-storage.interface';
import { RecordingService, RecordingSegment, RecordingState, RecordingInterruption } from './../../../core/services/recording.service';
import { FileDownloadService } from './../../../core/services/file-download.service';
import * as _ from 'lodash';
//import RecordRTC from '../../../../libs/recordrtc';
//declare const RecordRTC: any;

interface AudioSegment {
  id: string;
  srcBlob: Blob,
  fileName: string;
  uploadSuccess: boolean;
  blobName?: string;
  containerName?: string;
  transcript: string;
  processed: boolean;
  transcribeFailed: boolean;
  retryAttempts: number;
  timeoutHandle?: any;
  retryTimeoutHandle?: any;
  size?: any;
}

interface RecordingDebugConfig {
  reason?: string;
  groupId?: string;
  userId?: string;
  patientId?: string;
  type?: string;
  deviceConfig?: DebugDeviceConfig;
  recorderConfig?: RecordRTCDebugConfig;
  audioSegments?: any[];
  /** Set when a recording is declared stalled — see `markRecordingStalled()`. */
  stall?: RecordingStallDebug;
}

/**
 * What a stalled recording looked like at the moment it was noticed.
 *
 * Until this existed a stall left no server-side trace at all, so there was no way to answer
 * "how often is this happening?" except by reading Azure blob timestamps one consultation at a
 * time. `capturedSegments` against `elapsedMs` is the whole diagnosis: five segments against
 * 23 minutes is the segmentation loop having died. See `tasks/recording-segmentation-stall.md`.
 */
interface RecordingStallDebug {
  message?: string;
  /** What the recorder actually captured, in ms — the timer is stopped before this is read. */
  elapsedMs?: number;
  capturedSegments?: number;
  /** Segments the recorder should have produced by now, if it were healthy. */
  expectedSegments?: number;
  visibility?: string;
  detectedAt?: string;
}

interface RecordRTCDebugConfig {
  recorderType?: string;
  useLegacyRecorder?: boolean;
  mimeType?: string;
  logs?: any[];
}

interface DebugDeviceConfig {
  platform?: string;
  userAgent?: string;
  touchSupport?: boolean;
  isIOS?: boolean;
}

@Component({
  selector: 'app-ai-dialog',
  templateUrl: './ai-dialog.component.html',
  styleUrls: ['./ai-dialog.component.scss']
})
export class AiDialogComponent implements OnInit, OnDestroy {
  /** Marks where this work starts or finishes, for REMOAT's usage report. */
  private readonly usage = inject(UsageService);
  isMinimized = false;
  private originalSize!: { width: string; height: string };


  protected destroy$ = new Subject<void>();

  private subscriptions: Subscription[] = [];
  private appointments?: any[];
  private debugConfig: RecordingDebugConfig = {};
  private cachedPatientName?: string; // Cache patient name to avoid repeated lookups
  /**
   * The patient this consultation is being recorded against, for the title bar.
   *
   * Held as the object rather than a string so the template can use `patientHeading`, the same
   * pipe every other patient-scoped screen titles itself with.
   */
  patient: PatientNameParts | null = null;
  /**
   * Whether the Live Recording panel is open.
   *
   * Bound rather than hard-coded to `true` so `startRecording()` can force it open: the
   * accordion is `multi="false"`, so opening Audio Upload collapses this one, and an
   * auto-started recording (`data.startRecording`) could otherwise begin behind a collapsed
   * panel with no visible timer, level meter or Stop button.
   */
  liveRecordingExpanded = true;

  showNetworkWarning = false;
  isRunningSpeedTest = false;
  hasActiveNetworkConnection = true;
  speedResult: { down: number; up: number } | null = null;
  speedVerdict: { level: 'poor' | 'fair' | 'good' | 'great'; icon: string; msg: string } | null = null;
  cooldownRemaining = 0;          // seconds left before user may retest
  private cooldownTimer?: ReturnType<typeof setInterval>;
  private readonly COOLDOWN_SEC = 30;   // adjust as required
  retryEnabled = false;


  selectedTab = 0;

  rtcRef: any;
  useLegacyRecorder: boolean = false;
  mediaRecorder!: any;
  recordedChunks: Blob[] = [];
  audioBlob: any;
  audioUrl: string = '';
  recordingCompleted = false;
  isRecording = false;
  isPaused = false;

  /** Matches the selector in `_recording-alert.scss`. Set on the dialog's overlay pane. */
  private static readonly RECORDING_SHAKE_CLASS = 'ai-recording-shake';

  /** Mirrors whether this dialog currently holds the screen-border alarm. */
  private recordingAlertActive = false;
  private recordingAlertClosed = false;

  /**
   * True once the recorder has told us it is no longer capturing while we still believe a
   * consultation is being recorded — an audio-session interruption the native engine could
   * not resume, or a MediaStream the OS took away on web.
   *
   * It is a THIRD state, not a variant of paused: the session is still open and everything
   * captured before the loss is still recoverable, so Stop must stay available. Pausing and
   * resuming a recorder that no longer has a microphone cannot help, so those are disabled.
   */
  recordingStalled = false;
  recordingStalledMessage = '';
  /** Paused by the recorder itself because the microphone was taken (a phone call). */
  recordingInterrupted = false;

  recordingTime = 0;
  recordingInterval: any;
  // Wall-clock timing so the UI timer stays accurate when iOS suspends JS
  // timers during background. accumulatedMs keeps time banked across pauses;
  // resumedAt is the Date.now() of the most recent start/resume, or 0 when paused.
  private accumulatedMs = 0;
  private resumedAt = 0;
  stream: any;

  isPlaybackEnabled = false;
  isLoading = false;
  transcriptProcessed = false;
  transcript: string = '';
  downloadEnabled: boolean = false;
  disableAutoDownload: boolean = false;

  selectedFile: File | null = null;
  uploadStatus: string = '';
  aiStatus: string = '';
  uploadError: boolean = false;
  isDragOver: boolean = false;
  isProcessingCached: boolean = false;
  recordingId: any = null;
  fileTypeError: string = '';

  aiGenerate: boolean = true;
  generateTaskList: boolean = false;
  generateLetterOfMotivation: boolean = false;
  generateLetterToColleague: boolean = false;

  uploadCompleted: boolean = false;
  aiCompleted: boolean = false;
  aiError: boolean = false;
  requestId: any = null;
  finalResult: any;

  patientId?: string | null;
  groupId?: string | null;
  userId?: string | null;

  type: 'general' | 'ward-round' = 'general';

  timeout: any = undefined;
  reachedTimeout: boolean = false;
  sliceInterval: any = undefined;
  jobId?: string = undefined;
  /**
   * When this consultation started, fixed at the first write of its parent row.
   *
   * The row is now rewritten once per slice, and `createdAt: new Date()` on every one of those
   * writes would walk the recording's own start time forward a minute at a time — breaking the
   * newest-first sort in the Failed Recordings list, and the `createdAt` fallback
   * `reconcileAbandonedRecordings()` uses when a row has no heartbeat yet.
   */
  private recordingCreatedAt: string | null = null;

  tokenBalance?: number;
  lockProcessing: boolean = false;
  hasAiRequestsForToday: boolean = false;
  lastAiRequestTime: string = '';

  startTime: string = '';
  finishTime: string = '';

  private readonly MAX_RETRIES = 5;
  /** MatDialogRef.close before we patch it — used for real close + bypasses abandon guard. */
  private _matDialogCloseFn!: (result?: any) => void;
  private readonly RECORDING_SEGMENT_LENGTH = 60000;
  private readonly SEGMENT_TIMEOUT_MS = 60000;
  private readonly AI_STATUS_TIMEOUT_MS = 90000;
  private readonly AI_STATUS_POLL_INTERVAL_MS = 10000;
  private readonly SAFARI_TIMESLICE = 60000;

  /** Prevents parallel initializeMediaRecorder() runs (e.g. user clicks Record while ngOnInit still awaiting). */
  private mediaRecorderInitPromise: Promise<boolean> | null = null;

  isPremium: boolean = false;

  /** The patient's name under the header's label. */
  get patientName(): string {
    return patientFullName(this.patient);
  }

  constructor(
    public dialogRef: MatDialogRef<AiDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private route: ActivatedRoute,
    private overlay: OverlayContainer,
    private aiService: AIService,
    private feedbackService: FeedbackService,
    private patientNoteService: PatientNoteService,
    private patientAppointmentService: PatientAppointmentService,
    private noteTypeService: NoteTypeService,
    private logger: LoggerService,
    private audioService: AudioService,
    private usageService: TokenUsageService,
    private userService: UserService,
    private router: Router,
    private ngZone: NgZone,
    @Inject(PLATFORM_ID)
    private platformId: object,
    private analyticsService: AnalyticsService,
    private patientService: PatientService,
    private mediaService: MediaService,
    private net: NetworkConnectivityService,
    private speed: SpeedTestService,
    private patientAiService: PatientAiService,
    private groupService: GroupService,
    private recordingService: RecordingService,
    private fileDownloadService: FileDownloadService,
    private recordingAlert: RecordingAlertService,
    @Optional() private recordingStorageService?: RecordingStorageService, // Optional - non-breaking
  ) {
    console.log('🎯 ai-dialog constructor - RecordingStorageService injected:', !!this.recordingStorageService);
    if (this.recordingStorageService) {
      console.log('✅ RecordingStorageService is available in ai-dialog');
    } else {
      console.warn('⚠️ RecordingStorageService is NOT available in ai-dialog (might be injection issue)');
    }
  }

  async ngOnInit(): Promise<void> {
    this._matDialogCloseFn = this.dialogRef.close.bind(this.dialogRef);
    this.dialogRef.close = (result?: any) => {
      if (this.shouldWarnBeforeAbandon()) {
        void this.feedbackService
          .showConfirmation(
            'Leave AI Processing?',
            'You have work in progress. If you close now, you may lose unsaved progress.',
            undefined,
            'Stay',
            'Leave anyway',
            false
          )
          .then((leave) => {
            if (leave) {
              void this.runCloseSequence();
            }
          });
        return;
      }
      void this.runCloseSequence();
    };

    console.log('🎯 ai-dialog ngOnInit - RecordingStorageService:', !!this.recordingStorageService);

    // Catches the case service init cannot: a clinician who reloads within the heartbeat
    // window of a crash and comes straight back here to re-record. By now the heartbeat on the
    // row their dead tab left behind has aged past the threshold.
    this.recordingStorageService?.reconcileAbandonedRecordings().catch(err => {
      console.warn('Failed to reconcile abandoned recordings on open (non-critical):', err);
    });
    // Track that AI dialog is now open
    this.patientService.setAiDialogOpen(true);
    this.patientService.acquirePatientNavigationLock();
    this.resetAll();

    // Drop the alarm as the dialog starts closing rather than waiting for `ngOnDestroy`: the
    // nudge is a `transform` animation on the dialog container, and left in place it would
    // out-rank Material's own close animation on the same property.
    this.subscriptions.push(
      this.dialogRef.beforeClosed().subscribe(() => {
        this.recordingAlertClosed = true;
        this.syncRecordingAlert();
      })
    );

    if (this.data) {
      this.groupId = this.data.groupId || null;
      this.patientId = this.data.patientId || null;
      this.type = this.data.type || 'general';
      this.userId = this.data.userId || null;
      this.debugConfig.groupId = this.groupId ?? '';
      this.debugConfig.patientId = this.patientId ?? '';
      this.debugConfig.type = this.type ?? '';
    }

    // Prepare recorder before loadPatientName(): that HTTP await yields and the template becomes
    // interactive—if the user taps Record first, startRecording() must not be overwritten by a
    // second initializeMediaRecorder() when ngOnInit resumes.
    await this.initializeMediaRecorder();

    await this.loadPatientName();

    if (this.data?.startRecording) {
      console.log('Auto-starting recording due to startRecording parameter');
      this.toggleRecording();
    }

    this.subscriptions.push(
      this.route.queryParams
        .pipe(takeUntil(this.destroy$))
        .subscribe(params => {
          if (params['tab'] === 'audio-upload') {
            this.selectedTab = 1; // 'Audio Upload' tab
          }
        })
    );

    this.subscriptions.push(
      this.groupService.selectedGroup$
        .pipe(takeUntil(this.destroy$))
        .subscribe((group: any) => {
          this.isPremium = group?.isPremium === true;
          // Update lockProcessing - always false if premium
          if (this.isPremium) {
            this.lockProcessing = false;
          } else if (this.usageService.tokenBalanceSnapshot < 0) {
            this.lockProcessing = true;
          } else {
            this.lockProcessing = false;
          }
        })
    );

    this.subscriptions.push(
      this.patientAiService.patientAIRequests$
        .pipe(takeUntil(this.destroy$))
        .subscribe((aiRequests: any[] | null) => {
          this.updateAiRequestStatus(aiRequests);
        })
    );

    this.subscriptions.push(
      this.aiService.aiStatus$
        .pipe(takeUntil(this.destroy$))
        .subscribe((result: any) => {
          if (!result) return;

          console.log('aiStatus$', result);
          this.resetTimeout();

          this.timeout = setTimeout(() => {
            this.reachedTimeout = true;
            this.aiStatus = 'This process is taking longer than expected. There might be a delay in processing or an error could have occurred. We advise that you download your audio recording by clicking the download button below and then attempt to upload the audio file as a new AI Request. If you continue to experience this, please contact us for assistance.';
            this.debugConfig.reason = 'AI Status Timeout';
            this.debugConfig.audioSegments = this.audioSegments;
            this.aiService.sendDebugInformation(this.debugConfig).toPromise();
            this.openNetworkOverlay();
          }, this.AI_STATUS_TIMEOUT_MS);

          this.aiStatus = result.message || '';
          this.aiCompleted = result.completed;
          this.aiError = result.error === true;

          // Capture aiRequestId off any event that carries one (progress
          // notifications include it too) so the polling fallback below can
          // start before the completion event arrives — or substitute for it
          // if it never does.
          if (result.aiRequestId && !this.requestId) {
            this.requestId = result.aiRequestId;
          }

          if (this.aiCompleted) {
            this.requestId = result.aiRequestId || this.requestId;
            this.isLoading = false;
            // For ward rounds, request might be in result.request or result.payload?.request
            this.finalResult = result.request || result.payload?.request;
            //console.log('AI Completed - finalResult set to:', this.finalResult ? 'has value' : 'null/undefined', 'from result.request:', !!result.request, 'from result.payload?.request:', !!result.payload?.request);
            this.resetTimeout();
            this.aiService.isProcessing.next(false);

            // ✅ NEW: Immediately cleanup successful recording from IndexedDB
            this.cleanupOnSuccessfulProcessing().catch(err => {
              console.warn('Failed to cleanup successful recording (non-critical):', err);
            });
          }

          if (this.aiError) {
            this.aiService.isProcessing.next(false);
            this.resetTimeout();
            this.logger.triggerLogSend();
          }
        })
    );

    // Polling fallback: if the `ai-request-completed` socket message is silently
    // dropped, ask the server every 10s whether note type + notes have been
    // persisted (steps 1 + 2 — the only ones that gate the dialog's "completed"
    // state). When both are set, synthesize the completion event the socket
    // would have delivered. Ward rounds are no longer exempt: they create an
    // AIRequest row now (tasks/risk-management.md D1), with the note type set at
    // creation and the notes written before completion is sent, so both flags
    // this polls on become true on exactly the same schedule as any other
    // request. `runCloseSequence` re-fetches the row when the synthetic event
    // arrives without notes.
    this.subscriptions.push(
      interval(this.AI_STATUS_POLL_INTERVAL_MS)
        .pipe(
          takeUntil(this.destroy$),
          filter(() =>
            !!this.requestId &&
            !this.aiCompleted &&
            !this.aiError
          ),
          switchMap(() => this.patientAiService.getAIRequestStatus(this.requestId))
        )
        .subscribe({
          next: (response) => {
            if (this.aiCompleted || this.aiError) return;
            const data = response?.data;
            if (data?.hasNoteType && data?.hasNotes) {
              this.aiService.emitSyntheticCompletion(this.requestId);
            }
          },
          error: (err) => {
            console.warn('AI request status polling failed (non-critical):', err);
          }
        })
    );

    this.subscriptions.push(
      this.usageService.tokenBalance$
        .pipe(takeUntil(this.destroy$))
        .subscribe((result: any) => {
          this.tokenBalance = result;
          // Update lockProcessing - always false if premium
          if (!this.isPremium) {
            if (this.usageService.tokenBalanceSnapshot < 0) {
              this.lockProcessing = true;
            } else {
              this.lockProcessing = false;
            }
          }
        })
    );

    this.subscriptions.push(
      this.mediaService.transcriptionProgress$
        .pipe(takeUntil(this.destroy$))
        .subscribe((result: any) => {
          if (!result) return;
          this.uploadStatus = result?.processedChunks ? `Completed audio processing step ${result?.processedChunks} of ${result?.totalChunks}` : '';
          this.uploadCompleted = result?.processedChunks === result?.totalChunks;
          this.uploadError = result?.error === true;
        })
    );

    this.subscriptions.push(
      this.mediaService.transcriptionStatus$
        .pipe(takeUntil(this.destroy$))
        .subscribe((result: any) => {
          if (result) {
            console.log(`✅ Transcription received: ${result.transcript}`);
            this.handleTranscriptionResponse(result.jobId, result.transcript);
          }
        })
    );

    this.subscriptions.push(
      this.patientAppointmentService.patientAppointments$
        .pipe(takeUntil(this.destroy$))
        .subscribe(result => { if (result) this.appointments = result })
    );

    this.subscriptions.push(
      this.userService.userProfile$
        .pipe(takeUntil(this.destroy$))
        .subscribe(profile => {
          this.debugConfig.userId = profile?.id || '';
          this.userId = profile?.id || '';
          this.disableAutoDownload = profile?.disableAutoDownload || false;
        })
    );

    this.subscriptions.push(
      this.patientService.forceAiDialogRestore.asObservable().subscribe((forceRestore: boolean) => {
        if (forceRestore) {
          this.restore();
          this.patientService.forceAiDialogRestore.next(false);
        }
      })
    );

    // 🔔  When the browser flips fully offline
    this.net.isOffline$
      .pipe(takeUntil(this.destroy$))
      .subscribe(isOff => {
        this.hasActiveNetworkConnection = !isOff;

        if (isOff) {
          this.openNetworkOverlay();
          if (!this.isRecording) {
            this.downloadEnabled = true;
            this.reachedTimeout = true;
          }

        }
        this.maybeEnableRetryButton();
      });
  }

  ngOnDestroy(): void {
    this.patientService.releasePatientNavigationLock();
    // Track that AI dialog is now closed
    this.patientService.setAiDialogOpen(false);

    // Read off `this` BEFORE resetAll() below nulls them — see markRecordingAbandoned().
    if (this.isRecording && this.jobId) {
      this.markRecordingAbandoned(this.jobId).catch(err => {
        console.warn('Failed to mark recording abandoned on destroy (non-critical):', err);
      });
    }

    this.setStorageRefreshPaused(false);

    // Last resort, for a teardown that never went through `beforeClosed()` — a forced
    // navigation, say. A dialog destroyed mid-recording must not leave the border behind
    // with nothing recording.
    this.recordingAlertClosed = true;
    this.syncRecordingAlert();

    this.destroy$.next();
    this.destroy$.complete();

    this.teardownMediaRecorder();
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.subscriptions = [];

    this.stopRecordingInterval();
    this.stopStream();
    this.recordingId = null;
    this.finalResult = null;
    this.resetAll();

  }

  /**
   * True when closing the dialog or leaving the page could lose in-progress work.
   */
  private shouldWarnBeforeAbandon(): boolean {
    if (this.isRecording) {
      return true;
    }
    if (this.isLoading) {
      return true;
    }
    if (this.noteScanProcessing) {
      return true;
    }
    if (this.audioSegments.length > 0 && !this.uploadCompleted) {
      return true;
    }
    return false;
  }

  /**
   * Native leave confirmation when abandoning work.
   *
   * Deliberately does NOT touch storage. `beforeunload` fires *before* the clinician is asked
   * whether to leave, so anything written here is written even when they choose to stay — the
   * old code stopped the recorder outright at this point, leaving a live-looking dialog in
   * front of a dead recorder. `pagehide` below is the hook that only fires if the page really
   * does go away.
   */
  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.shouldWarnBeforeAbandon()) {
      event.preventDefault();
      event.returnValue = '';
    }
  }

  /**
   * The teardown signal that actually fires.
   *
   * `beforeunload` is unreliable on WebKit and is skipped entirely when a tab is discarded
   * under memory pressure — which is the most likely way the 2026-09-10 consultation ended.
   * `pagehide` fires in both cases, and is the last event a page is guaranteed to see.
   *
   * Unlike `beforeunload` this fires only once the page is genuinely going, so a clinician who
   * chooses to stay never reaches it.
   *
   * A `persisted: true` pagehide is the bfcache, not a teardown: the page is coming back. The
   * status is set anyway rather than special-cased, because the next slice's heartbeat write
   * repairs it if capture is still running when it returns, and being wrong in the recoverable
   * direction is the whole design here.
   */
  @HostListener('window:pagehide')
  onPageHide(): void {
    if (this.isRecording && this.jobId) {
      this.markRecordingAbandoned(this.jobId).catch((error) => {
        console.warn('Failed to mark recording abandoned on pagehide:', error);
      });
    }
  }

  minimize() {
    const pane = this.overlay
      .getContainerElement()
      .querySelector('.cdk-overlay-pane')! as HTMLElement;

    // 1) snapshot original size so we can restore later
    const orig = pane.getBoundingClientRect();
    if (!this.originalSize) {
      this.originalSize = {
        width: `${orig.width}px`,
        height: `${orig.height}px`
      };
    }

    // 2) tell Material to shrink to 300×69px (this applies inline style instantly)
    this.dialogRef.updateSize('300px', '69px');

    // 3) on the next paint, measure the new position & size, then move it
    setTimeout(() => {
      requestAnimationFrame(() => {
        const now = pane.getBoundingClientRect();
        console.log('[NOW]: ', now);

        // target is 10px from left, 10px from bottom
        const targetX = 10;
        const targetY = window.innerHeight - 10 - now.height;
        console.log('[TARGET Y]: ', targetY);
        // how far from its current top/left?
        const dx = targetX - now.left;
        const dy = targetY - now.top;

        pane.style.transform = `translate(${dx}px, ${dy}px)`;
      });
    }, 150);

    this.isMinimized = true;
  }

  restore() {
    const pane = this.overlay
      .getContainerElement()
      .querySelector('.cdk-overlay-pane')! as HTMLElement;

    // 1) clear the transform — animates back to its original spot
    pane.style.transform = '';

    // 2) restore the original size
    this.dialogRef.updateSize(
      this.originalSize.width,
      this.originalSize.height
    );

    this.isMinimized = false;
  }

  handleTranscriptionResponse(jobId: string, transcript: string) {
    console.log(`✅ Received transcription for Job ID: ${jobId}`);

    const segment = this.audioSegments.find(seg => seg.id === jobId);
    if (segment) {
      segment.transcript = transcript;
      segment.processed = true;
      if (segment.timeoutHandle) {
        clearTimeout(segment.timeoutHandle);
        delete segment.timeoutHandle;
      }
      segment.transcribeFailed = false;
      console.log(`✅ Updated segment ${segment.fileName} with transcript`);

      // ✅ Persist transcript to IndexedDB so it's available for retry
      this.updateSegmentInIndexedDB(segment).catch(err => {
        console.warn('Failed to persist segment transcript to IndexedDB (non-critical):', err);
      });
    } else {
      console.warn(`⚠️ No matching segment found for jobId: ${jobId}`);
    }

    // 🔹 Ensure all segments are processed before compiling
    const allProcessed = this.audioSegments.every(seg => seg.processed);
    const pendingCount = this.audioSegments.filter(seg => !seg.processed).length;

    console.log(`🟡 Pending Transcriptions: ${pendingCount}`);
    if (pendingCount > 0) {
      console.log('Pending segments snapshot:', JSON.parse(JSON.stringify(this.audioSegments)));
    }

    if (allProcessed && !this.isRecording) {
      console.log("✅ All segments processed. Compiling final transcript...");
      this.processCompiledTranscript();
    } else if (!this.isRecording) {
      this.retryFailedSegments();
    }
  }


  private resetTimeout() {
    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = undefined;
      this.reachedTimeout = false;
    }
  }

  private updateAiRequestStatus(aiRequests: any[] | null): void {
    if (!aiRequests || aiRequests.length === 0) {
      this.hasAiRequestsForToday = false;
      this.lastAiRequestTime = '';
      return;
    }

    const today = moment();
    const aiRequestsForToday = aiRequests.filter((request: any) =>
      // is the request from today but not within the last 5 minutes (to prevent the new request from triggering the warning)
      moment(request.createdAt).isSame(today, 'day') &&
      moment(request.createdAt).isAfter(moment().subtract(5, 'minutes'))
    );

    if (aiRequestsForToday.length > 0) {
      this.hasAiRequestsForToday = true;
      const mostRecentRequest = _.maxBy(aiRequestsForToday, 'createdAt');
      this.lastAiRequestTime = moment(mostRecentRequest.createdAt).format('HH:mm');
    } else {
      this.hasAiRequestsForToday = false;
      this.lastAiRequestTime = '';
    }
  }

  /* private isIOS(): boolean {
    const isIOS = [
      'iPad Simulator',
      'iPhone Simulator',
      'iPod Simulator',
      'iPad',
      'iPhone',
      'iPod',
    ].includes(navigator.platform)
      // iPad on iOS 13 detection
      || (navigator.userAgent.includes("Mac") && "ontouchend" in document);
    this.debugConfig.deviceConfig = {
      platform: navigator.platform,
      userAgent: navigator.userAgent,
      touchSupport: "ontouchend" in document,
      isIOS: isIOS
    }
    return isIOS
  } */

  private isIOS(): boolean {
    const ua = navigator.userAgent || '';
    const platform = navigator.platform || '';
    const touchable = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

    /* 1. Classic iOS devices or mobile UA */
    let isiOS = /iPad|iPhone|iPod/i.test(platform) ||
      /CriOS|FxiOS|EdgiOS|OPiOS/i.test(ua);      // iOS Chrome/Firefox/Edge

    /* 2. iPadOS 13 + spoofs Mac but has touch */
    if (!isiOS && platform === 'MacIntel' && touchable) {
      isiOS = true;
    }

    /* optional: write to debugConfig if it already exists */
    if (this.debugConfig) {
      this.debugConfig.deviceConfig = {
        platform,
        userAgent: ua,
        touchSupport: touchable,
        isIOS: isiOS
      };
    }

    return isiOS;
  }

  /* async initializeMediaRecorder(): Promise<boolean> {
    try {
      if (isPlatformBrowser(this.platformId)) {
        const RecordRTC = await import('src/libs/recordrtc') as any;
        const recorder = RecordRTC.default || RecordRTC;

        try {
          const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
          this.stream = stream;


          this.debugConfig.recorderConfig = {};

          // base configuration for windows, android, etc.
          let recorderType = RecordRTC.MediaStreamRecorder;

          this.debugConfig.recorderConfig.recorderType = 'MediaStreamRecorder';
          this.debugConfig.recorderConfig.useLegacyRecorder = false;

          // config for chrome on ios
          if (this.isIOS()) {
            recorderType = RecordRTC.StereoAudioRecorder;
            this.useLegacyRecorder = true;

            this.debugConfig.recorderConfig.recorderType = 'StereoAudioRecorder';
            this.debugConfig.recorderConfig.useLegacyRecorder = true;

            console.log('Using StereoAudioRecorder for iOS');
          }

          // pick the best mimeType for this browser
          const candidates = [
            'audio/webm',
            'audio/webm;codecs=pcm',
            'audio/wav',
            'audio/ogg',
            'audio/mp4',
          ];
          let mimeType = candidates.find(m => MediaRecorder.isTypeSupported(m)) as "audio/webm" | "audio/webm;codecs=pcm" | "audio/wav" | "audio/ogg" | "audio/mp4" | undefined;

          if (!mimeType) {
            console.error('No supported MIME type for MediaRecorder');
            mimeType = 'audio/wav';
            //return false;
          }

          const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
          const needsWavFallback = isSafari && typeof window.MediaRecorder === 'undefined';
          //config for safari on ios that has MediaRecorder support
          if (isSafari && !needsWavFallback) {
            mimeType = 'audio/mp4';
            recorderType = RecordRTC.MediaStreamRecorder;
            this.useLegacyRecorder = false;

            this.debugConfig.recorderConfig.recorderType = 'MediaStreamRecorder';
            this.debugConfig.recorderConfig.useLegacyRecorder = true;

            console.log('Using MediaStreamRecorder for safari on iOS');
          }

          // ios safari without MediaRecorder support
          if (needsWavFallback) {
            recorderType = RecordRTC.StereoAudioRecorder;
            mimeType = 'audio/wav';
            this.useLegacyRecorder = true;        // because StereoAudioRecorder needs it
          }

          console.log('Supported mimeType:', mimeType);
          this.debugConfig.recorderConfig.mimeType = mimeType;

          if (this.useLegacyRecorder) {
            this.mediaRecorder = new recorder(stream, {
              type: 'audio',
              recorderType,
              mimeType: mimeType as any,
              numberOfAudioChannels: 1,
              audioBitsPerSecond: 64000,
              disableLogs: false,
              timeSlice: this.RECORDING_SEGMENT_LENGTH,
              ondataavailable: (blob: Blob, final: boolean) => {
                const wallClock = new Date().toISOString();
                const estDurationSec = blob.type === 'audio/wav' && blob.size > 44
                  ? ((blob.size - 44) / (44100 * 2)).toFixed(2) + 's (est. 44.1kHz mono)'
                  : 'n/a';
                console.log('got a blob slice from ondataavailable', { wallClock, size: blob.size, type: blob.type, estDurationSec, isFinal: final });
                this.handleSegmentUploadAndTranscription(blob);
                if (final) {
                  this.finalizeRecording();
                }


              }
            });
          } else {
            this.mediaRecorder = new recorder(stream, {
              type: 'audio',
              recorderType,
              mimeType: mimeType as any,
              numberOfAudioChannels: 1,
              audioBitsPerSecond: 64000,
              disableLogs: false,
            });
          }
          console.log('Media Recorder Initialized');
          return true;
        } catch (error) {
          console.error('Error accessing microphone:', error);
          return false;
        }
      } else {
        return false;
      }
    } catch (error) {
      console.error('Failed to initialize media recorder', error);
      return false;
    }
  } */

  async initializeMediaRecorder(): Promise<boolean> {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }
    return true;
  }

  private teardownMediaRecorder(): void {
    this.recordingService.cleanup();
  }


  resetAll() {
    this.ngZone.run(() => {
      // Cleared BEFORE the teardown: `RecordingService.cleanup()` emits a final state change
      // of `isRecording: false`, and `handleRecorderState` would read that as lost capture if
      // this dialog still believed a recording was running.
      this.isRecording = false;
      this.isPaused = false;
      this.clearRecordingStall();
      this.syncRecordingAlert();

      this.teardownMediaRecorder();
      this.jobId = undefined;
      this.recordingCompleted = false;
      this.audioBlob = null;
      this.audioUrl = '';
      this.isPlaybackEnabled = false;
      this.stopRecordingInterval();
      this.recordingTime = 0;
      this.isLoading = false;
      this.aiCompleted = false;
      this.uploadCompleted = false;
      this.aiStatus = '';
      this.uploadStatus = '';
      this.requestId = null;
      this.finalResult = null;
      this.aiService.resetStatus();
      this.patientId = null;
      this.patient = null;
      this.type = 'general';
      this.resetTimeout();
      this.stopStream();
      clearInterval(this.sliceInterval);
      this.sliceInterval = undefined;
      this.audioSegments.forEach(seg => {
        if (seg.timeoutHandle) {
          clearTimeout(seg.timeoutHandle);
          delete seg.timeoutHandle;
        }
        if (seg.retryTimeoutHandle) {
          clearTimeout(seg.retryTimeoutHandle);
          delete seg.retryTimeoutHandle;
        }
      });
      this.audioSegments = [];
      this.liveRecordingExpanded = true;
      this.recordingCreatedAt = null;
      this.downloadEnabled = false;
      this.startTime = '';
      this.finishTime = '';
      this.transcriptProcessed = false;
      this.mediaService.resetStatus();
      this.reachedTimeout = false;
      this.useLegacyRecorder = false;
      this.debugConfig = {};
      this.showNetworkWarning = false;
      this.isRunningSpeedTest = false;
      this.hasActiveNetworkConnection = true;
      this.speedResult = null;
      clearInterval(this.cooldownTimer as any);
      this.retryEnabled = false;
      this.hasAiRequestsForToday = false;
      this.lastAiRequestTime = '';
      this.cachedPatientName = undefined;
    });
  }

  private stopStream() {
    if (this.stream) {
      try {
        this.stream.getAudioTracks().forEach((track: any) => {
          track.stop();
          console.log('Audio track stopped:', track);
        });
        this.stream = undefined;
        console.log('Audio stream stopped and set to undefined.');
      } catch (error) {
        console.error('Error stopping audio tracks:', error);
      }
    } else {
      console.log('No audio stream to stop.');
    }
  }

  toggleRecording() {
    try {
      // Pause/resume cannot revive a recorder whose microphone has gone; the only useful
      // action left is Stop, which keeps everything captured before the loss.
      if (this.recordingStalled) return;

      if (this.isRecording) {
        if (!this.isPaused) {
          void this.pauseRecording();
        } else {
          void this.resumeRecording();
        }
      } else {
        void this.startRecording();
      }
    } catch (error) {
      console.error('failed to toggle recording', error);
    }
  }

  async startRecording() {
    this.analyticsService.trackButtonClick(`AI Dialog Start Recording ${this.type}`);
    try {
      this.recordingService.configure({
        mode: 'segmented',
        segmentLengthMs: this.RECORDING_SEGMENT_LENGTH,
        onSegment: (seg: RecordingSegment) => {
          this.ngZone.run(() => {
            this.handleSegmentUploadAndTranscription(seg.blob);
            if (seg.isFinal) {
              this.finalizeRecording();
            }
          });
        },
        onStateChange: (state: RecordingState) => {
          this.ngZone.run(() => this.handleRecorderState(state));
        },
        onError: (err: string) => {
          this.ngZone.run(() => this.handleRecorderError(err));
        },
        onInterruption: (event: RecordingInterruption) => {
          this.ngZone.run(() => this.handleRecorderInterruption(event));
        }
      });

      this.jobId = this.aiService.generateRandomSocketId(20);
      this.mediaService.subscribeToTranscriptionResults(this.jobId);
      if (this.startTime === '') {
        this.startTime = moment().format('HH:mm');
      }

      const started = await this.recordingService.startRecording();
      if (!started) {
        console.error('RecordingService failed to start');
        return;
      }

      this.startRecordingInterval();
      console.log('Started recording...');
      this.isRecording = true;
      this.usage.flow('consult note', 'start');
      // The controls, the timer and Stop all live inside that panel.
      this.liveRecordingExpanded = true;
      this.isPaused = false;
      this.recordingInterrupted = false;
      this.clearRecordingStall();
      this.syncRecordingAlert();
      // After the recorder has actually started, so the chime means "you are recording" and
      // not "you pressed a button" — first use on a device can sit on a mic-permission
      // prompt for several seconds.
      this.recordingAlert.playStartChime();
      this.setStorageRefreshPaused(true);
      this.markAppointmentAsStarted();

      // The parent row exists from second one, not from the moment the recording finishes.
      // Everything that used to write it — a normal stop, a detected stall, the teardown save —
      // depends on this component still being alive to do the writing, and on 2026-09-10 a page
      // that died without warning took a 23-minute consultation with it precisely because no
      // row was ever written. A row in 'recording' state is self-describing: whatever kills the
      // page, the next load knows what it was looking at.
      this.storeRecordingInIndexedDB('recording').catch(err => {
        console.warn('Failed to open recording row in IndexedDB (non-critical):', err);
      });
    } catch (error) {
      console.error('failed to start recording', error);
    }
  }

  /**
   * The recorder's 1 Hz heartbeat. Its only job here is to notice a stop nobody asked for.
   *
   * Both recorder implementations keep their session open when capture dies underneath them
   * — on purpose, so whatever was already captured can still be stopped and kept — so this
   * event is the only signal that the microphone has gone. The guards below mean a state of
   * `isRecording: false` is only believed while this dialog thinks a recording is genuinely
   * running: `stopRecording()` clears `isRecording` before it calls the service, and
   * `resetAll()` clears it before tearing the recorder down, so neither of those deliberate
   * stops can be mistaken for a stall.
   */
  private handleRecorderState(state: RecordingState): void {
    if (!this.isRecording || this.isPaused) return;
    if (this.recordingStalled) {
      // Audio is flowing again, so the stall no longer holds.
      if (state.isRecording) {
        this.clearRecordingStall();
        this.syncRecordingAlert();
        this.startRecordingInterval();
      }
      return;
    }
    if (state.isRecording) return;

    this.markRecordingStalled(
      'Recording stopped unexpectedly — your device stopped sending audio. Everything captured up to this point is safe. Press stop to save and process it.'
    );
  }

  /**
   * The microphone was taken mid-recording (a phone call, a headset unplugged). The recorder has
   * already handed over everything captured so far and paused itself; this mirrors that pause, so
   * Pause/Resume keep working. Native recorders carry on by themselves when the call ends.
   */
  private handleRecorderInterruption(event: RecordingInterruption): void {
    if (!this.isRecording) return;

    if (event.state === 'began') {
      if (this.isPaused) return; // already paused by the user; they resume when ready
      this.recordingInterrupted = true;
      this.isPaused = true;
      this.stopRecordingInterval();
      this.clearRecordingStall();
      this.syncRecordingAlert();
      this.feedbackService.showToast(
        'Recording paused because the microphone was interrupted (for example by a phone call). ' +
        'Everything recorded so far is saved. Press Resume to carry on.'
      );
      this.persistRecordingSnapshot().catch(err => {
        console.warn('Failed to snapshot interrupted recording (non-critical):', err);
      });
      return;
    }

    if (event.resumed && this.recordingInterrupted) {
      this.recordingInterrupted = false;
      this.isPaused = false;
      this.syncRecordingAlert();
      this.startRecordingInterval();
      this.feedbackService.showToast('Recording resumed.');
    }
  }

  private handleRecorderError(err: string): void {
    console.error('[AI Dialog] recording error:', err);

    if (typeof err === 'string' && err.startsWith('CAPTURE_LOST')) {
      /*
       * THE SAME GUARD `handleRecorderState` OPENS WITH, and its absence here is why every
       * press of Stop raised "Recording stopped unexpectedly - the audio stream ended".
       *
       * The root cause was in `RecordingService` — releasing the microphone ends its tracks,
       * the MediaStream fires `inactive`, and the capture watcher could not tell that apart
       * from the microphone being pulled out — and it is fixed there. This is the second lock
       * on the same door: `stopRecording()` clears `isRecording` before it calls the service,
       * so no deliberate stop can reach the banner however the recorder reports itself.
       *
       * A loss that genuinely coincides with the press of Stop is suppressed by this, and that
       * is the right trade: the user is stopping anyway, and everything captured is kept.
       */
      if (!this.isRecording || this.isPaused || this.recordingStalled) return;

      const reason = err.slice('CAPTURE_LOST:'.length).trim();
      this.markRecordingStalled(
        `Recording stopped unexpectedly — ${reason || 'your device stopped sending audio'}. ` +
        'Everything captured up to this point is safe. Press stop to save and process it.'
      );
      return;
    }

    // Anything else — WRITE_FAILED, ROLLOVER_FAILED, a failed resume after an interruption —
    // is still worth showing. Until now every recorder error went to the console and nowhere
    // else, so a failing recording looked identical to a working one. These do not
    // necessarily mean capture has stopped, so they get a toast rather than the banner.
    if (this.isRecording) {
      this.feedbackService.showToast(`Recording problem: ${err}`);
    }
  }

  /**
   * Freezes the UI at the truth and gets what we have onto disk.
   *
   * The timer is stopped first, so `recordingTime` reports what was actually captured
   * instead of continuing to climb over dead air — that clock is the whole reason this
   * failure went unnoticed. The snapshot then writes the recording's metadata row, which is
   * otherwise only written when a recording finishes normally; without it a consultation
   * interrupted here leaves segments in IndexedDB with no parent record.
   */
  private markRecordingStalled(message: string): void {
    if (this.recordingStalled) return;

    this.recordingStalled = true;
    this.recordingStalledMessage = message;
    this.syncRecordingAlert();
    this.stopRecordingInterval();

    this.feedbackService.showToast(message);

    this.persistRecordingSnapshot().catch(err => {
      console.warn('Failed to snapshot stalled recording (non-critical):', err);
    });

    this.reportStall(message);
  }

  /**
   * Tells the server a recording stalled, so this stops being invisible in aggregate.
   *
   * Best-effort and deliberately unawaited: a stall is already a bad moment and a failing
   * debug POST must not make it worse. `recordingTime` is read after `stopRecordingInterval()`
   * above, so it reports what was captured rather than wall clock.
   */
  private reportStall(message: string): void {
    try {
      this.debugConfig.reason = 'Recording Stalled';
      // A compact projection, not the segments themselves: `/ai/debug` emails its payload, and
      // a 23-minute consultation's segment array carries every transcript with it.
      this.debugConfig.audioSegments = this.audioSegments.map(seg => ({
        id: seg.id,
        size: seg.size,
        uploadSuccess: seg.uploadSuccess,
        processed: seg.processed,
        transcribeFailed: seg.transcribeFailed,
        retryAttempts: seg.retryAttempts
      }));
      this.debugConfig.stall = {
        message,
        elapsedMs: this.recordingTime,
        capturedSegments: this.audioSegments.length,
        expectedSegments: Math.floor(this.recordingTime / this.RECORDING_SEGMENT_LENGTH),
        visibility: typeof document !== 'undefined' ? document.visibilityState : undefined,
        detectedAt: new Date().toISOString()
      };
      this.aiService.sendDebugInformation(this.debugConfig).toPromise()
        .catch((err: any) => console.warn('Failed to report stall (non-critical):', err));
    } catch (error) {
      console.warn('Failed to assemble stall report (non-critical):', error);
    }
  }

  private clearRecordingStall(): void {
    this.recordingStalled = false;
    this.recordingStalledMessage = '';
  }

  /**
   * Raises or drops the "you are recording" alarm — a red border around the whole viewport
   * plus a periodic nudge of this dialog — to match the recorder's real state.
   *
   * The alarm follows capture, not the dialog: paused and stalled both drop it, because a
   * border that stays up while nothing is being recorded is the one thing that would teach
   * clinicians to ignore it. `recordingAlertActive` keeps this dialog's claim on the
   * (reference counted, since two AI dialogs can be open) service balanced no matter how
   * many times the state transitions call through here.
   */
  private syncRecordingAlert(): void {
    const shouldAlert = !this.recordingAlertClosed && this.isRecording && !this.isPaused && !this.recordingStalled;
    if (shouldAlert === this.recordingAlertActive) {
      return;
    }

    this.recordingAlertActive = shouldAlert;

    if (shouldAlert) {
      this.recordingAlert.activate();
    } else {
      this.recordingAlert.deactivate();
    }

    try {
      if (shouldAlert) {
        this.dialogRef.addPanelClass(AiDialogComponent.RECORDING_SHAKE_CLASS);
      } else {
        this.dialogRef.removePanelClass(AiDialogComponent.RECORDING_SHAKE_CLASS);
      }
    } catch (error) {
      // `resetAll()` runs from `ngOnDestroy`, by which point the CDK may already have
      // disposed the overlay pane these classes live on. The border above is what matters;
      // losing the nudge on a dialog that no longer exists is not worth an exception.
      console.warn('Could not toggle the recording nudge on the dialog panel:', error);
    }
  }

  /**
   * Writes what exists right now to IndexedDB without touching the recorder.
   *
   * Unlike `saveInProgressRecordingBeforeUnload()` this does NOT stop the recording — the
   * decision to end a consultation stays with the clinician. Segments are already stored as
   * they are cut, so the load-bearing part is `storeRecordingInIndexedDB()`.
   */
  private async persistRecordingSnapshot(): Promise<void> {
    if (!this.recordingStorageService || !this.jobId) return;

    for (const segment of this.audioSegments) {
      await this.storeSegmentInIndexedDB(segment).catch((err: any) => {
        console.warn('Failed to store segment in snapshot:', err);
      });
    }

    await this.storeRecordingInIndexedDB().catch((err: any) => {
      console.warn('Failed to store recording metadata in snapshot:', err);
    });
  }

  private markAppointmentAsStarted() {
    try {
      if (!this.patientId) {
        console.warn('Cannot mark appointment as started: patientId is not available');
        return;
      }

      this.patientAppointmentService.markAppointmentsAsStarted(this.patientId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (result) => {
            console.log('Appointments marked as started:', result);
          },
          error: (error) => {
            console.error('Failed to mark appointment as started:', error);
          }
        });
    } catch (error) {
      console.error('Failed to mark appointment as started:', error);
    }
  }

  // Ends today's appointments for the patient (else yesterday's) on the server. Only the end time is written: sending
  // the cached appointment back would overwrite a note that was saved since it was cached.
  private markAppointmentAsCompleted() {
    if (!this.patientId) return;
    const sub: Subscription = this.patientAppointmentService.markAppointmentsAsEnded(this.patientId).subscribe({
      next: () => sub.unsubscribe(),
      error: () => console.error('Failed to mark appointment as completed'),
    });
  }

  /**
   * `isPaused` is set BEFORE the await, for the same reason the native service sets its own
   * flag before the bridge call: the recorder reports the pause as a `stateChange` of
   * `isRecording: false`, and on native that event can arrive while this method is still
   * suspended. Setting the flag afterwards leaves a window in which `handleRecorderState`
   * would read a deliberate pause as lost capture. Rolled back if the call fails.
   */
  async pauseRecording() {
    this.analyticsService.trackButtonClick(`AI Dialog Pause Recording ${this.type}`);
    this.isPaused = true;
    this.syncRecordingAlert();
    try {
      console.log('Paused recording...');
      await this.recordingService.pauseRecording();
      this.stopRecordingInterval();
    } catch (error) {
      this.isPaused = false;
      this.syncRecordingAlert();
      console.error('failed to pause media recorder', error);
    }
  }

  async resumeRecording() {
    this.analyticsService.trackButtonClick(`AI Dialog Resume Recording ${this.type}`);
    try {
      console.log('Resumed recording...');
      await this.recordingService.resumeRecording();
      // After an interruption the microphone may still be unavailable; the recorder has said why.
      if (this.recordingService.isPaused()) return;
      this.isPaused = false;
      this.recordingInterrupted = false;
      this.syncRecordingAlert();
      this.startRecordingInterval();
    } catch (error) {
      console.error('failed to resume media recorder', error);
    }
  }


  async stopRecording() {
    this.analyticsService.trackButtonClick(`AI Dialog Stop Recording ${this.type}`);
    try {
      console.log('Stopped recording called...');
      if (this.isMinimized) {
        this.restore();
      }
      this.isRecording = false;
      this.isPaused = false;
      this.syncRecordingAlert();
      this.stopRecordingInterval();
      this.recordingTime = 0;
      this.accumulatedMs = 0;
      this.resumedAt = 0;
      this.isLoading = true;
      this.uploadStatus = 'Preparing audio file for upload.'

      // A recorder paused by an interruption has already handed over all it had, so no final
      // segment follows either.
      const wasStalled = this.recordingStalled || this.recordingInterrupted;
      this.recordingInterrupted = false;
      await this.recordingService.stopRecording();

      // `finalizeRecording()` normally runs off the final segment's `isFinal` flag. A stalled
      // recorder may have nothing left to flush, so that segment never arrives and the dialog
      // would sit on "Preparing audio file for upload." for ever. The grace period lets a
      // real final segment win the race — on native it arrives as a separate bridge message,
      // after stop() has already resolved.
      if (wasStalled) {
        setTimeout(() => {
          if (!this.recordingCompleted) {
            console.warn('[AI Dialog] no final segment after a stalled stop; finalizing anyway');
            this.finalizeRecording();
          }
        }, 3000);
      }
    } catch (error) {
      console.error('failed to stop media recorder', error);
    }

  }

  finalizeRecording = () => {
    // Reachable from two places now — the final segment and the stalled-stop fallback above.
    if (this.recordingCompleted) return;

    this.setStorageRefreshPaused(false);
    this.recordingCompleted = true;
    this.downloadEnabled = true;
    this.resetTimeout();
    this.timeout = setTimeout(() => {
      this.reachedTimeout = true;
      this.aiStatus = 'This process is taking longer than expected. There might be a delay in processing or an error could have occurred. We advise that you download your audio recording by clicking the download button below and then attempt to upload the audio file as a new AI Request. If you continue to experience this, please contact us for assistance.';
      this.debugConfig.reason = 'AI Status Timeout';
      this.debugConfig.audioSegments = this.audioSegments;
      this.aiService.sendDebugInformation(this.debugConfig).toPromise();
      this.openNetworkOverlay();
    }, this.AI_STATUS_TIMEOUT_MS)
    /* if (!this.disableAutoDownload) {
      this.downloadCurrentAudioFile();
    } */
    this.finishTime = moment().format('HH:mm');

    // ✅ NEW: Store recording metadata in IndexedDB (NON-BLOCKING)
    // This runs asynchronously and doesn't affect the existing flow
    this.storeRecordingInIndexedDB().catch(err => {
      console.warn('Failed to store recording in IndexedDB (non-critical):', err);
    });
  }

  startRecordingInterval() {
    this.resumedAt = Date.now();
    this.recordingInterval = setInterval(() => {
      this.recordingTime = this.accumulatedMs + (Date.now() - this.resumedAt);
    }, 1000);
  }

  stopRecordingInterval() {
    if (this.resumedAt) {
      this.accumulatedMs += Date.now() - this.resumedAt;
      this.resumedAt = 0;
      this.recordingTime = this.accumulatedMs;
    }
    clearInterval(this.recordingInterval);
  }

  onFileSelected(event: any) {
    const file = event.target.files[0];
    this.onFullAudioFileSelected(file);
  }

  onDragOver(event: any) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: any) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: any) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
    const files = event.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      this.onFullAudioFileSelected(file);
    }
  }

  isAudioFile(file: File): boolean {
    const type = file.type?.toLowerCase() || '';
    const name = file.name?.toLowerCase() || '';

    console.log('File type:', type);
    console.log('File name:', name);

    return (
      type === '' || // iOS Safari
      type.startsWith('audio/') ||
      type === 'video/mp4' ||
      name.endsWith('.wav') || // fallback if type missing or incorrect
      name.endsWith('.mp3') ||
      name.endsWith('.m4a')
    );
  }

  // For large/long files that the server (worker) will split
  onFullAudioFileSelected(file: any) {
    if (!file) return;

    // The Audio Upload panel is disabled while capture runs, but the guard belongs here too:
    // a drop lands on whatever is under the cursor, and this is also reachable from the file
    // input directly. Uploading a file mid-consultation would queue a second job against the
    // same dialog and leave the live recording with nowhere to go.
    if (this.isRecording) {
      this.feedbackService.showToast('Stop the current recording before uploading an audio file.');
      return;
    }

    if (this.isAudioFile(file)) {

      this.selectedFile = file;

      this.fileTypeError = '';

      // e.g. If the user is explicitly choosing a "full file" approach:
      this.uploadFullAudioFile(file);
    } else {
      this.selectedFile = null;
      this.fileTypeError = 'Please select a valid audio file.';
      console.error('An invalid audio file was selected');
    }
  }

  /**
   * This method handles the entire large file upload flow:
   * 1) Request a SAS URL from the server
   * 2) Upload entire file
   * 3) Queue the job with jobType = 'fullFile'
   */
  async uploadFullAudioFile(file: File) {
    try {
      this.isLoading = true;
      this.uploadStatus = 'Preparing audio file for upload...';

      // Generate a random jobId so the worker can reference it
      this.jobId = this.aiService.generateRandomSocketId(20);
      this.mediaService.subscribeToTranscriptionResults(this.jobId);

      const { uploadUrl, blobName, containerName } = await this.mediaService
        .getUploadUrl(this.patientId!, this.jobId, file.name)
        .toPromise();

      // Step 2: Upload entire file
      this.uploadStatus = 'Uploading...';
      await this.mediaService.uploadFileToAzure(file, uploadUrl).toPromise();

      // Step 3: Queue the job with jobType = 'fullFile'
      this.uploadStatus = 'Processing file...';
      const segment: AudioSegment = {
        id: this.jobId,
        srcBlob: file,
        fileName: file.name,
        transcript: '',
        processed: false,
        uploadSuccess: true,
        blobName: blobName,
        containerName: containerName,
        retryAttempts: 0,
        transcribeFailed: false,
        size: file.size
      };
      this.audioSegments.push(segment);

      await this.mediaService
        .notifyServerFileUploaded(this.jobId, this.jobId, blobName, containerName, this.patientId!, this.groupId!, this.userId!, 'full')
        .toPromise();

      //this.uploadStatus = 'Full audio file queued for transcription successfully.';
      console.log('Full audio file uploaded & queued successfully.');
      this.isRecording = false;
      this.syncRecordingAlert();
      //this.isLoading = false;
    } catch (error) {
      console.error('Error uploading full audio file:', error);
      this.uploadStatus = 'Error uploading audio file.';
      this.isLoading = false;
    }
  }

  uploadAudio() {
    if (this.selectedFile) {
      this.processNewFileUpload();
    }
  }

  processNewFileUpload() {
    this.analyticsService.trackButtonClick(`AI Dialog File Upload ${this.type}`);
    console.log(`Processing audio file for ${this.type}`);
    if (this.selectedFile) {
      this.isLoading = true;
      const isWardRound = this.type === 'ward-round';
      this.aiService.isProcessing.next(true);
      this.aiService.generateNotesFromAudio(this.selectedFile, !this.aiGenerate, this.patientId, isWardRound, undefined, this.jobId, this.startTime, this.finishTime)
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          (result: any) => {
            this.markAppointmentAsCompleted();
          },
          (error: any) => {
            this.aiService.isProcessing.next(false);
            this.isLoading = false;
            console.error('Upload error:', error);
            this.uploadStatus = 'Error uploading audio';
            this.logger.triggerLogSend();
          }
        );
    }
  }

  audioSegments: AudioSegment[] = [];
  handleSegmentUploadAndTranscription(audioBlob: Blob, previousSocketId?: string) {
    if (!audioBlob.size) {
      console.error('Audio blob is empty, TODO: skip upload and report to the user.');
      //return;
    }
    /* const timeStamp = moment().valueOf();
    const mimeType = audioBlob.type;
    let fileExtension = '';
    console.log('handleSegmentUploadAndTranscription for audio blob type:', mimeType);

    switch (mimeType) {
      case 'audio/webm':
      case 'audio/webm;codecs=pcm':
        fileExtension = 'webm';
        break;
      case 'audio/mp4':
        fileExtension = 'mp4';
        break;
      case 'audio/ogg':
        fileExtension = 'ogg';
        break;
      case 'audio/wav':
      default:
        fileExtension = 'wav';
        break;
    }

    const fileName = `${timeStamp}_slice.${fileExtension}`; */

    const timeStamp = moment().valueOf();
    const rawType = audioBlob.type || '';
    // drop any parameters, e.g. ";codecs=…"
    const mime = rawType.split(';')[0];

    // derive the extension from the mime’s subtype
    let fileExtension = mime.split('/')[1] || 'wav';

    // normalize a few common oddballs
    if (fileExtension === 'x-m4a') fileExtension = 'mp4';
    if (fileExtension === 'mpeg') fileExtension = 'mp3';
    if (!['webm', 'wav', 'mp4', 'ogg', 'mp3'].includes(fileExtension)) {
      fileExtension = 'wav';
    }

    const fileName = `${timeStamp}_slice.${fileExtension}`;
    const socketId = previousSocketId || this.aiService.generateRandomSocketId(20);

    let segment: AudioSegment;
    if (!previousSocketId) {
      segment = {
        id: socketId,
        srcBlob: audioBlob,
        fileName,
        transcript: '',
        uploadSuccess: false,
        blobName: '',
        containerName: '',
        processed: false,
        retryAttempts: 0,
        transcribeFailed: false,
        size: audioBlob.size
      };
      this.audioSegments.push(segment);
    } else {
      segment = this.audioSegments.find(s => s.id === socketId)!;
    }

    const audioFile = new File([audioBlob], fileName, { type: rawType });

    // ✅ NEW: Store segment in IndexedDB (NON-BLOCKING, optional)
    // This runs in parallel and doesn't affect the upload flow
    this.storeSegmentInIndexedDB(segment).catch(err => {
      // Silently log - don't interrupt recording flow
      console.warn('Failed to store segment in IndexedDB (non-critical):', err);
    });

    // Keeps the parent row's segment list, duration and — while capture is running — its
    // heartbeat current, so a tab that dies between here and the next slice loses at most one
    // minute of bookkeeping. Skipped once the recording has stopped: from that point the
    // status is derived from the segments and this must not force it back to 'recording'.
    if (this.isRecording) {
      this.storeRecordingInIndexedDB('recording').catch(err => {
        console.warn('Failed to refresh recording row in IndexedDB (non-critical):', err);
      });
    }

    // EXISTING CODE - unchanged
    this.uploadSegmentToAzure(segment, audioFile);

    // start the timer
    this.scheduleTimeout(segment, audioFile);
  }

  private async uploadSegmentToAzure(segment: AudioSegment, audioFile: File) {
    const patientId = this.patientId!;
    const jobId = this.jobId!;
    const socketId = segment.id;

    // If the recording session has been torn down (dialog closed / resetAll ran),
    // patientId and jobId are cleared. Abort silently rather than firing a doomed
    // request with patientId: null and rescheduling endless 10s retries.
    if (!patientId || !jobId) {
      console.debug('Skipping segment upload; recording session is no longer active.', { fileName: segment.fileName });
      if (segment.retryTimeoutHandle) {
        clearTimeout(segment.retryTimeoutHandle);
        delete segment.retryTimeoutHandle;
      }
      return;
    }

    try {
      //this.uploadStatus = 'Uploading audio segment...';

      // Step 1: Fetch upload URL
      const { uploadUrl, blobName, containerName } =
        await this.mediaService.getUploadUrl(patientId, jobId, segment.fileName).toPromise();

      //this.uploadStatus = 'Uploading segment to Azure...';

      // Step 2: Upload file with retries
      await this.mediaService.uploadFileToAzure(audioFile, uploadUrl).toPromise();

      //this.uploadStatus = 'Segment uploaded. Queuing for transcription...';

      // Step 3: Notify backend to queue transcription
      await this.mediaService.notifyServerFileUploaded(jobId, socketId, blobName, containerName, patientId, this.groupId!, this.userId!, 'segment').toPromise();

      //segment.processed = false;
      segment.uploadSuccess = true;
      segment.blobName = blobName;
      segment.containerName = containerName;
      segment.transcribeFailed = false;

      //this.uploadStatus = 'Segment queued for transcription successfully';

      console.log(`Segment ${segment.fileName} uploaded and queued successfully.`);

      // If recording has completed, ensure all segments are processed or retried
      if (this.recordingCompleted) {
        this.markAppointmentAsCompleted();
        this.retryFailedSegments();
      }

    } catch (error) {
      console.error('Segment upload or queuing failed:', error);
      segment.transcribeFailed = true;
      this.retrySegmentUpload(segment, audioFile);

      // ✅ NEW: Mark recording as failed in IndexedDB (NON-BLOCKING)
      // Run async, don't wait - doesn't affect retry logic
      this.markRecordingFailureInIndexedDB(this.jobId!, {
        error: error instanceof Error ? error.message : 'Upload failed',
        errorCode: (error as any)?.status || (error as any)?.code,
        failedSegmentId: socketId
      }).catch(err => {
        console.warn('Failed to mark recording as failed in IndexedDB (non-critical):', err);
      });

      // Also update segment status (non-blocking)
      this.updateSegmentInIndexedDB(segment).catch(err => {
        console.warn('Failed to update segment status in IndexedDB (non-critical):', err);
      });
    }
  }

  private scheduleTimeout(segment: AudioSegment, audioFile: File) {
    if (segment.timeoutHandle) {
      clearTimeout(segment.timeoutHandle);
    }
    segment.timeoutHandle = setTimeout(() => {
      if (!segment.processed) {
        console.warn(`⚠️ Timeout for ${segment.fileName}, retrying…`);
        this.retrySegmentUpload(segment, audioFile);
      }
    }, this.SEGMENT_TIMEOUT_MS);
  }


  private retrySegmentUpload(segment: AudioSegment, audioFile: File) {
    if (segment.retryAttempts >= this.MAX_RETRIES) {
      console.error(`Segment ${segment.fileName} failed after maximum retries.`);
      segment.processed = true;
      if (segment.timeoutHandle) {
        clearTimeout(segment.timeoutHandle);
        delete segment.timeoutHandle;
      }
      if (segment.retryTimeoutHandle) {
        clearTimeout(segment.retryTimeoutHandle);
        delete segment.retryTimeoutHandle;
      }
      this.maybeEnableRetryButton();
      this.maybeEnableDownloadOnRetryFailures();
      return;
    }

    segment.retryAttempts++;
    //const retryDelay = 1000 * Math.pow(2, segment.retryAttempts); // Exponential backoff

    //console.log(`Retrying upload for ${segment.fileName} in ${retryDelay / 1000}s...`);
    console.log(`Retrying upload for ${segment.fileName} in 10s...`);

    segment.retryTimeoutHandle = setTimeout(() => {
      delete segment.retryTimeoutHandle;
      this.uploadSegmentToAzure(segment, audioFile);
      // **Re‑arm the 60 s transcription‑arrival timer**
      this.scheduleTimeout(segment, audioFile);
    }, 10000); // 10 seconds for retry
  }

  retryFailedSegments() {

    // any segment that failed too many times should be considered "done"
    // to avoid infinite waiting in processCompiledTranscript()
    this.audioSegments.forEach(seg => {
      if (!seg.processed && seg.retryAttempts >= this.MAX_RETRIES) {
        console.warn(`Segment ${seg.fileName} failed after ${this.MAX_RETRIES} retries; marking as processed.`);
        seg.processed = true;
        if (seg.timeoutHandle) {
          clearTimeout(seg.timeoutHandle);
          delete seg.timeoutHandle;
        }
        this.maybeEnableRetryButton();
      }
    });

    //const pendingSegments = this.audioSegments.filter(segment => !segment.processed && segment.retryAttempts < 5);
    const pending = this.audioSegments.filter(seg => !seg.processed);

    if (pending.length === 0 && !this.isRecording) {
      // ✅ Only mark processing as completed when ALL segments are either processed or exhausted retries
      this.uploadStatus = 'Audio file processing completed';
      this.uploadCompleted = true;
      this.processCompiledTranscript(); // ✅ Process after ALL results (not just retries)
      return;
    }

    // Retry only failed segments, but ensure pending ones are still tracked
    const failedSegments = pending.filter(segment => segment.transcribeFailed);

    if (failedSegments.length > 0) {
      failedSegments.forEach(segment => {
        const audioFile = new File([segment.srcBlob], segment.fileName, { type: segment.srcBlob.type });
        this.retrySegmentUpload(segment, audioFile);
      });
    }


  }

  private async compileTranscript(): Promise<string> {
    let transcript = '';
    for (let slice of this.audioSegments) {
      transcript += slice.transcript + ' ';
    }
    return transcript;
  }

  async processCompiledTranscript() {
    // skip if we already kicked it off
    if (this.transcriptProcessed) return;

    if (this.audioSegments.length === 0) {
      console.warn("No audio segments to process.");
      return;
    }

    // check only for segments still legitimately awaiting retries
    const stillWaiting = this.audioSegments.some(
      s => !s.processed && s.retryAttempts < this.MAX_RETRIES
    );
    if (stillWaiting) {
      console.warn("⏳ Waiting for segments before final transcript…");
      return;
    }

    this.transcriptProcessed = true;
    this.isLoading = true;
    this.uploadStatus = 'File processing completed....';
    this.uploadCompleted = true;
    this.uploadError = false;

    const transcript = await this.compileTranscript();
    console.log("✅ Final compiled transcript:", transcript);

    this.aiService.isProcessing.next(true);
    const isWardRound = this.type === 'ward-round';



    this.aiService.generateNotesFromAudio(null, !this.aiGenerate, this.patientId, isWardRound, transcript, this.jobId, this.startTime, this.finishTime)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (result: any) => {
          this.debugConfig.audioSegments = this.audioSegments;
          this.debugConfig.reason = 'AI Notes Generated Successfully';
          //this.aiService.sendDebugInformation(this.debugConfig).toPromise();
        },
        (error) => {
          try {
            console.error('❌ Error processing compiled transcription:', JSON.stringify(error));
          }
          catch (err) {
            console.error('❌ Error processing compiled transcription:', error);
          }
          this.uploadStatus = 'Error uploading audio';
          this.uploadError = true;
          this.logger.triggerLogSend();
          this.isLoading = false;
        }
      );
  }



  noteScanFileObjects: FileObject[] = [];
  noteScanUrl: string = '';
  noteScanProcessing: boolean = false;

  private resetNoteScanning() {
    this.noteScanFileObjects = [];
    this.noteScanUrl = '';
    this.noteScanProcessing = false;
  }

  onNoteScanUpload(files: FileObject[]) {
    this.resetNoteScanning();

    if (files.length > 0) {
      this.noteScanFileObjects = [{
        ...files[0],
        //url: URL.createObjectURL(files[0]),
        type: 'image'
      }];
      this.uploadImage();
    }
  }

  onNoteScanDelete(fileObject: FileObject) {
    this.resetNoteScanning();
  }


  async uploadImage() {
    this.analyticsService.trackButtonClick(`AI Dialog Image Extraction ${this.type}`);
    this.noteScanProcessing = true;
    const formData = new FormData();
    if (this.noteScanFileObjects.length > 0) {
      formData.append('image', await this.blobToFile(this.noteScanFileObjects[0].url, `${moment().valueOf()}.${this.noteScanFileObjects[0].type}`));
    }

    this.aiService.uploadImage(formData)
      .pipe(
        takeUntil(this.destroy$),
        switchMap((result: any) => {
          this.noteScanUrl = result.imageUrl;
          return this.aiService.extractNoteDetailsFromImage(this.groupId!, this.noteScanUrl, true);
        })
      )
      .subscribe({
        next: (result: any) => {

          this.resetNoteScanning();

          const extractedResult = result.data;

          if (extractedResult.date) {
            extractedResult.date = moment.utc(extractedResult.date, 'DD/MM/YYYY').startOf('day');
          }

          const noteType = this.noteTypeService.getWardRoundNoteType();

          const note = {
            date: extractedResult.date,
            title: 'Ward Round',
            notes: extractedResult.notes,
            detectedNoteType: noteType
          }

          this.patientNoteService.setEditingNote(note);
          this.router.navigate(['/patients', {
            outlets: {
              patient: ['add-note']
            }
          }], {
            queryParams: {
              patientId: this.patientId,
            }
          });
          this.noteScanProcessing = false;
          this._matDialogCloseFn(null);
          this.resetAll();
        },
        error: (error) => {
          this.resetNoteScanning();
          this.noteScanProcessing = false;
          console.error(error);
          this.feedbackService.showToast('There was an error extracting information from the provided image. Please try again.');
        }
      });
  }


  private async blobToFile(url: string, defaultFileName: string) {
    const response = await fetch(url);
    const blob = await response.blob();
    const contentType = blob.type;

    let extension = '';
    if (contentType === 'image/png') {
      extension = '.png';
    } else if (contentType === 'image/jpeg' || contentType === 'image/jpg') {
      extension = '.jpg';
    } else {
      throw new Error('Unsupported image format: ' + contentType);
    }

    const fileName = defaultFileName.replace(/\.[^/.]+$/, extension); // Replace the default extension with the correct one
    return new File([blob], fileName, { type: contentType });
  }

  async closeDialog() {
    if (this.shouldWarnBeforeAbandon()) {
      const leave = await this.feedbackService.showConfirmation(
        'Leave AI Processing?',
        'You have work in progress. If you close now, you may lose unsaved progress.',
        undefined,
        'Stay',
        'Leave anyway',
        false
      );
      if (!leave) {
        return;
      }
    }

    await this.runCloseSequence();
  }

  /**
   * Full close path (IndexedDB cleanup, ward-round navigation, overlay close, analytics).
   * Invoked by the Close button / template after confirm, and by the patched dialogRef.close
   * (e.g. MatDialog.closeAll(), ESC) after confirm when abandon warning applies.
   */
  private async runCloseSequence(): Promise<void> {
    // Delete stored segments and recording from IndexedDB when user deletes
    if (this.recordingStorageService && this.jobId && this.isRecording) {
      try {
        await this.recordingStorageService.deleteRecording(this.jobId);
        console.log('✅ Deleted recording and segments from IndexedDB on dialog close:', this.jobId);
      } catch (error) {
        // Log but don't throw - cleanup failure is non-critical
        console.warn('Failed to delete recording from IndexedDB on dialog close (non-critical):', error);
      }
    }

    const isWardRound = this.type === 'ward-round';
    if (isWardRound) {
      const noteType = this.noteTypeService.getWardRoundNoteType();
      const wardRoundRequest = await this.resolveWardRoundRequest();
      const note = {
        title: 'Ward Round',
        notes: wardRoundRequest ? (wardRoundRequest.notes ?? '') : this.finalResult,
        detectedNoteType: noteType,
        // Captured before resetAll() below clears this.jobId. Undefined for older/unstarted recordings.
        recordingJobId: this.jobId ?? null,
        // Ward rounds create an AIRequest now (tasks/risk-management.md D1), so the note can
        // finally point back at its own provenance — and carry the extracted facility issues
        // for workstream F to render on the note.
        aiRequestId: wardRoundRequest?.id ?? this.requestId ?? null,
        suggestedRiskItems: this.extractSuggestedRiskItems(wardRoundRequest)
      }
      this.patientNoteService.setEditingNote(note);
      this.router.navigate(['/patients', {
        outlets: {
          patient: ['add-note']
        }
      }], {
        queryParams: {
          patientId: this.patientId,
        }
      });
      this._matDialogCloseFn(null);
      this.resetAll();
      this.logger.triggerLogSend();
    } else {
      this._matDialogCloseFn(this.finalResult || null);
      this.resetAll();
      this.logger.triggerLogSend();
    }

    if (!this.finalResult) {
      this.analyticsService.trackButtonClick(`AI Dialog Closed Early ${this.type}`);
    }

  }

  /**
   * Resolve the ward round's AIRequest for the close path.
   *
   * `finalResult` is polymorphic by history: since tasks/risk-management.md D1 the completion
   * event carries the serialized AIRequest object, but two other shapes still reach here —
   * a bare notes string (an older GenerateWardRoundOutput still deployed), and the id-only stub
   * `{ id }` that `AIService.emitSyntheticCompletion` produces when the polling fallback fires
   * because the socket event was dropped. The stub has no notes, so it is re-fetched; without
   * that the note would be saved empty, which is worse than the pre-D1 behaviour.
   *
   * Returns null when there is no request object to work from, in which case the caller falls
   * back to treating `finalResult` as the notes string.
   */
  private async resolveWardRoundRequest(): Promise<any | null> {
    const result = this.finalResult;
    if (!result || typeof result !== 'object') {
      return null;
    }

    if (typeof result.notes === 'string' && result.notes.length > 0) {
      return result;
    }

    const id = result.id ?? this.requestId;
    if (!id) {
      return result;
    }

    try {
      const response: any = await this.patientAiService.getPatientAIRequestById(id).toPromise();
      return response?.data ?? result;
    } catch (error) {
      console.warn('Failed to re-fetch ward round AI request before navigating to add-note:', error);
      return result;
    }
  }

  /**
   * Pull the AI's suggested risk items off a request in the one shape the rest of the app uses.
   *
   * The column is nullable and stays NULL for every request predating it and every recording
   * whose extraction found nothing, so NULL — not just `[]` — is the normal case. Anything that
   * is not a well-formed `{ items: [...] }` collapses to an empty array rather than being
   * passed on: these end up on a doctor's letterhead, so a malformed blob must render as
   * "nothing found", never as a half-parsed suggestion.
   */
  private extractSuggestedRiskItems(request: any | null): any[] {
    const raw = request?.suggestedRiskItems;
    if (!raw) {
      return [];
    }
    if (Array.isArray(raw)) {
      return raw;
    }
    return Array.isArray(raw.items) ? raw.items : [];
  }

  async downloadCurrentAudioFile() {
    this.analyticsService.trackButtonClick(`AI Dialog Download Recording ${this.type}`);

    // Show confirmation if:
    // 1. Processing is still active, OR
    // 2. We're in a retry/failure state but haven't given up yet
    const shouldConfirm = (!this.finalResult && !this.uploadCompleted) ||
      (this.isLoading && this.audioSegments.length > 0);

    if (shouldConfirm) {
      const confirmed = await this.feedbackService.showConfirmation(
        'Download Audio Recording',
        'Downloading the audio file will stop the current processing. You will need to reupload the recording to continue with AI processing. Are you sure you want to download the audio file now?',
        undefined,
        'Cancel',
        'Download'
      );

      if (!confirmed) {
        return;
      }
    }

    await this.attemptFileRebuildFromMemory();
    return;
  }

  private async attemptFileRebuildFromMemory() {
    try {
      const blobs = this.audioSegments.map(segment => segment.srcBlob);
      const audioBuffers = await Promise.all(blobs.map(blob => this.audioService.decodeAudioData(blob)));
      const concatenatedBuffer = this.audioService.concatenateAudioBuffers(audioBuffers);
      const stitchedBlob = await this.audioService.encodeAudioBufferToBlob(concatenatedBuffer);

      const mimeType = blobs[0].type;
      /* let fileExtension = '';
      switch (mimeType) {
        case 'audio/webm':
        case 'audio/webm;codecs=pcm':
          fileExtension = 'webm';
          break;
        case 'audio/mp4':
          fileExtension = 'mp4';
          break;
        case 'audio/ogg':
          fileExtension = 'ogg';
          break;
        case 'audio/wav':
        default:
          fileExtension = 'wav';
          break;
      } */

      // drop any parameters, e.g. ";codecs=…"
      const mime = mimeType.split(';')[0];
      // derive the extension from the mime’s subtype
      let fileExtension = mime.split('/')[1] || 'wav';

      // normalize a few common oddballs
      if (fileExtension === 'x-m4a') fileExtension = 'mp4';
      if (fileExtension === 'mpeg') fileExtension = 'mp3';
      if (!['webm', 'wav', 'mp4', 'ogg', 'mp3'].includes(fileExtension)) {
        fileExtension = 'wav';
      }

      let file;

      if (stitchedBlob) {
        const patient = this.patientService.patient;
        const fileName = patient ? `${patient.name} ${patient.surname} - ${moment().format('YYYYMMDD')} - recording.${fileExtension}` : `${moment().format('YYYYMMDD-HH:mm')}_${this.jobId}_recording.${fileExtension}`;
        file = new File([stitchedBlob], fileName, { type: mimeType });
      } else {
        file = this.selectedFile;
      }

      if (file) {
        await this.fileDownloadService.download(file, file.name);
      }

    } catch (error) {
      console.error('failed to download current file from memory: ', error);
    }
  };

  private maybeEnableRetryButton(): void {

    const stuck = this.audioSegments.some(s =>
      (!s.uploadSuccess || !s.processed) &&          // still not done
      s.retryAttempts >= this.MAX_RETRIES);          // …and out of tries

    this.retryEnabled = stuck && !this.isRecording;

  }

  private maybeEnableDownloadOnRetryFailures(): void {
    if (this.audioSegments.length === 0) return;

    // Calculate retry failure rate
    const totalSegments = this.audioSegments.length;
    const failedSegments = this.audioSegments.filter(seg =>
      seg.transcribeFailed && seg.retryAttempts >= 2
    ).length;

    const failureRate = failedSegments / totalSegments;

    // Enable download based on failure patterns
    if (failureRate >= 0.5 && totalSegments >= 2) {  // 50% failure rate with 2+ segments
      console.log(`🔄 High failure rate detected (${Math.round(failureRate * 100)}%) - enabling download`);
      this.downloadEnabled = true;
      this.reachedTimeout = true;
    }

    // Also enable if we have segments that have exhausted most retries
    const nearExhausted = this.audioSegments.filter(seg =>
      seg.retryAttempts >= this.MAX_RETRIES - 1 && !seg.processed
    ).length;

    if (nearExhausted >= 2) {
      console.log(`🔄 Multiple segments near retry exhaustion - enabling download`);
      this.downloadEnabled = true;
      this.reachedTimeout = true;
    }
  }

  async retryProcessing(): Promise<void> {
    this.showNetworkWarning = false;
    this.retryEnabled = false;     // hide button while we’re working
    this.isLoading = true;
    this.uploadError = false;      // reset banner
    this.uploadStatus = 'Re-running AI processing…';
    this.aiStatus = '';


    const reuploadTasks: Promise<void>[] = [];

    for (const seg of this.audioSegments) {
      /* A. was never uploaded */
      if (!seg.uploadSuccess && seg.retryAttempts >= this.MAX_RETRIES) {
        seg.retryAttempts = 0;                       // give it a fresh slate
        const file = new File([seg.srcBlob], seg.fileName,
          { type: seg.srcBlob.type });
        reuploadTasks.push(this.uploadSegmentToAzure(seg, file));
        continue;
      }

      /* B. uploaded but never transcribed */
      if (seg.uploadSuccess && !seg.processed) {
        reuploadTasks.push(
          this.mediaService
            .notifyServerFileUploaded(
              this.jobId!,          // jobId == socketId
              seg.id,
              seg.blobName!,
              seg.containerName!,
              this.patientId!,
              this.groupId!,
              this.userId!,
              'segment')           // or 'full'
            .toPromise()
            .catch(() => { seg.transcribeFailed = true; })
        );
      }
    }

    await Promise.all(reuploadTasks);

    this.isLoading = false;
    this.retryFailedSegments();    //-> will either finish or light the button again
  }

  shouldShowCloseButton(): boolean {

    if (this.type !== 'ward-round') {
      if (this.isRecording) {
        // show delete button
        return true;
      }
      if (this.finalResult) {
        // show view button
        return true;
      }
      if (this.uploadCompleted) {
        // show close button
        return true;
      }
      if (!this.isLoading) {
        // show close button by default
        return true;
      }
      // hide the button in other cases
      return false;

    } else if (this.type === 'ward-round') {

      if (this.isRecording) {
        // show delete button
        return true;
      }

      if (!this.isRecording && !this.finalResult && this.uploadStatus) {
        // hide button if recording is processing
        return false;
      }

      if (!this.isRecording && !this.noteScanProcessing) {
        // show close button by default
        return true;
      }
      // hide the button in other cases
      return false;

    } else {
      return true;
    }
  }

  private openNetworkOverlay() {
    if (!this.showNetworkWarning) {
      this.showNetworkWarning = true;
      this.speedResult = null;
    }
  }

  async runSpeedTest(): Promise<void> {
    if (this.isRunningSpeedTest || this.cooldownRemaining > 0) return;

    this.isRunningSpeedTest = true;
    try {
      this.speedResult = await this.speed.run();
      this.updateVerdict();
    } finally {
      this.isRunningSpeedTest = false;
      this.startCooldown();
    }
  }

  /** Prevent repeated tests for COOLDOWN_SEC seconds */
  private startCooldown(): void {
    this.cooldownRemaining = this.COOLDOWN_SEC;

    // clear any previous timer
    clearInterval(this.cooldownTimer as any);

    this.cooldownTimer = setInterval(() => {
      this.cooldownRemaining--;
      if (this.cooldownRemaining <= 0) {
        clearInterval(this.cooldownTimer as any);
      }
    }, 1000);
  }

  updateVerdict(): void {
    const down = this.speedResult?.down ?? 0;
    const up = this.speedResult?.up ?? 0;

    /* thresholds – adjust as needed */
    if (down < 2 || up < 1) {
      this.speedVerdict = {
        level: 'poor',
        icon: 'signal_wifi_off',
        msg: 'Insufficient bandwidth detected; audio uploads are unlikely to complete.'
      };
    } else if (down < 10 || up < 3) {
      this.speedVerdict = {
        level: 'fair',
        icon: 'network_check',
        msg: 'Limited bandwidth detected; transcription and note generation may be delayed.'
      };
    } else if (down < 50 || up < 10) {
      this.speedVerdict = {
        level: 'good',
        icon: 'wifi',
        msg: 'Adequate bandwidth detected; standard processing expected.'
      };
    } else {
      this.speedVerdict = {
        level: 'great',
        icon: 'wifi_tethering',
        msg: 'Optimal bandwidth detected; full functionality available.'
      };
    }
  }


  async handleNetworkDecision(shouldDownload: boolean) {
    if (shouldDownload) {
      await this.downloadCurrentAudioFile();   // already exists
    }
    this.showNetworkWarning = false;
  }

  // ✅ Load and cache patient name once (called in ngOnInit)
  private async loadPatientName(): Promise<void> {
    if (!this.patientId) {
      this.cachedPatientName = undefined;
      this.patient = null;
      return;
    }

    try {
      const patient = await this.patientService.getPatientById(this.patientId, true).toPromise();
      this.patient = patient ?? null;
      // `patientFullName` rather than `${name} ${surname}`: it skips empty parts, so a patient
      // with no surname no longer lands in IndexedDB as "Ada undefined", and the name stored
      // against the recording matches the one in the title bar exactly.
      this.cachedPatientName = patientFullName(this.patient) || undefined;
      if (this.cachedPatientName) {
        console.log('✅ Cached patient name:', this.cachedPatientName);
      }
    } catch (error) {
      // Silently fail - patient name is optional
      this.cachedPatientName = undefined;
      this.patient = null;
    }
  }

  // ✅ NEW: Helper method for non-blocking storage
  // The failed-recordings scan walks the entire segments store, and storing a segment
  // schedules one. During a consultation that is once a minute, for a badge the user cannot
  // act on until the recording ends - so suspend it while recording and let the resume run a
  // single refresh. Idempotent, so pause/resume can never go out of balance.
  private storageRefreshPaused = false;

  private setStorageRefreshPaused(paused: boolean): void {
    if (!this.recordingStorageService || paused === this.storageRefreshPaused) {
      return;
    }
    this.storageRefreshPaused = paused;
    if (paused) {
      this.recordingStorageService.pauseFailedRecordingsRefresh();
    } else {
      this.recordingStorageService.resumeFailedRecordingsRefresh();
    }
  }

  private async storeSegmentInIndexedDB(segment: AudioSegment): Promise<void> {
    // Check if service is available (optional injection)
    if (!this.recordingStorageService) {
      console.debug('RecordingStorageService not available (optional injection)');
      return; // Gracefully skip if service not available
    }

    if (!this.jobId) {
      console.debug('jobId not available yet, skipping IndexedDB storage');
      return;
    }

    try {
      console.log('✅ Storing segment in IndexedDB:', segment.id, 'for recording:', this.jobId);

      const storageSegment: StorageAudioSegment = {
        id: segment.id,
        recordingId: this.jobId,
        blob: segment.srcBlob,
        fileName: segment.fileName,
        mimeType: segment.srcBlob.type || 'audio/wav',
        order: this.audioSegments.indexOf(segment),
        size: segment.size || segment.srcBlob.size,
        uploadSuccess: segment.uploadSuccess || false,
        processed: segment.processed || false,
        transcribeFailed: segment.transcribeFailed || false,
        retryAttempts: segment.retryAttempts || 0,
        transcript: segment.transcript, // ✅ Include transcript so it's persisted
        // ✅ Include recording metadata so segments can be identified even if orphaned
        patientId: this.patientId || undefined,
        patientName: this.cachedPatientName, // ✅ Use cached patient name
        groupId: this.groupId || undefined,
        userId: this.debugConfig.userId || this.userId || undefined,
        type: this.type || 'general',
        startTime: this.startTime,
        finishTime: this.finishTime
      };

      await this.recordingStorageService.storeSegment(storageSegment);
      console.log('✅ Successfully stored segment in IndexedDB:', segment.id);
    } catch (error) {
      // Log but don't throw - storage failure shouldn't break recording
      console.warn('❌ IndexedDB storage failed (non-critical):', error);
      // Recording continues normally
    }
  }

  // ✅ NEW: Helper method for non-blocking failure marking
  private async markRecordingFailureInIndexedDB(recordingId: string, errorInfo: { error: string; errorCode?: number; failedSegmentId?: string }): Promise<void> {
    if (!this.recordingStorageService) return;

    try {
      await this.recordingStorageService.markRecordingAsFailed(recordingId, errorInfo);
    } catch (error) {
      console.warn('IndexedDB error marking failure (non-critical):', error);
    }
  }

  // ✅ NEW: Helper method for non-blocking segment update
  private async updateSegmentInIndexedDB(segment: AudioSegment): Promise<void> {
    if (!this.recordingStorageService) return;

    try {
      await this.storeSegmentInIndexedDB(segment);
    } catch (error) {
      console.warn('IndexedDB error updating segment (non-critical):', error);
    }
  }

  /**
   * Writes the recording's parent row.
   *
   * @param statusOverride forces the stored status. `'recording'` is written from
   * `startRecording()` and refreshed as each segment is cut; it is what lets a consultation
   * survive a page that dies without warning — see `FailedRecording.status` and
   * `RecordingStorageService.reconcileAbandonedRecordings()`. Omit it to let the status be
   * derived from the segments, which is what a normal finish does.
   */
  private async storeRecordingInIndexedDB(statusOverride?: FailedRecording['status']): Promise<void> {
    // Skip if service not available
    if (!this.recordingStorageService) {
      console.debug('RecordingStorageService not available (optional injection)');
      return;
    }

    if (!this.jobId || !this.patientId || !this.groupId || !this.userId) {
      console.debug('Missing required data for IndexedDB storage:', { jobId: this.jobId, patientId: this.patientId, groupId: this.groupId, userId: this.userId });
      return;
    }

    try {
      console.log('✅ Storing recording metadata in IndexedDB:', this.jobId);
      // Collect all segment IDs
      const segmentIds = this.audioSegments.map(seg => seg.id);

      // Determine if recording failed
      // Check for upload failures - any segment that hasn't uploaded successfully
      const hasUploadFailures = this.audioSegments.some(seg => !seg.uploadSuccess);
      // Check for transcription failures - segments that explicitly failed transcription
      const hasTranscriptionFailures = this.audioSegments.some(seg => seg.transcribeFailed);
      // Check if segments have exhausted retries
      const hasExhaustedRetries = this.audioSegments.some(seg =>
        !seg.processed && seg.retryAttempts >= this.MAX_RETRIES
      );
      const hasFailures = hasTranscriptionFailures || hasExhaustedRetries;

      const allProcessed = this.audioSegments.every(seg =>
        seg.processed || seg.retryAttempts >= this.MAX_RETRIES
      );

      // Get user ID from debugConfig (already set in ngOnInit)
      const userId = this.debugConfig.userId || '';

      // Calculate total duration in seconds (recordingTime is in milliseconds)
      const totalDurationSeconds = Math.round(this.recordingTime / 1000);

      const recording: FailedRecording = {
        id: this.jobId,
        jobId: this.jobId,
        sessionId: this.recordingId || this.jobId,
        patientId: this.patientId,
        patientName: this.cachedPatientName, // ✅ Use cached patient name
        groupId: this.groupId,
        userId,
        type: this.type || 'general',
        segmentIds,
        totalDuration: totalDurationSeconds, // Convert milliseconds to seconds
        startTime: this.startTime,
        finishTime: this.finishTime,
        status: statusOverride ?? (hasFailures ? 'failed' : (allProcessed && this.uploadCompleted ? 'completed' : 'pending')),
        // Only meaningful while the status is 'recording'; it is how another tab, or this one
        // after a reload, tells a live capture from one whose tab died.
        heartbeatAt: new Date().toISOString(),
        uploadFailed: hasUploadFailures, // True if ANY segment hasn't uploaded successfully
        transcriptionFailed: hasTranscriptionFailures, // True if ANY segment failed transcription
        failedSegments: this.audioSegments.filter(seg => seg.transcribeFailed || !seg.uploadSuccess).map(seg => seg.id),
        createdAt: this.recordingCreatedAt ?? new Date().toISOString(),
        version: 1
      };
      this.recordingCreatedAt = recording.createdAt;

      await this.recordingStorageService.storeRecording(recording);
      console.log('✅ Successfully stored recording metadata in IndexedDB:', this.jobId);
    } catch (error) {
      // Log but don't throw - storage failure shouldn't break recording
      console.warn('❌ IndexedDB storage error (non-critical):', error);
    }
  }

  // ✅ NEW: Cleanup recording from IndexedDB on successful processing
  // Called when finalResult is set (audio processing completed successfully)
  private async cleanupOnSuccessfulProcessing(): Promise<void> {
    if (!this.recordingStorageService || !this.jobId) {
      return;
    }

    try {
      // Immediately remove successful recording from IndexedDB
      await this.recordingStorageService.deleteRecording(this.jobId);
      console.log('Successfully cleaned up recording from IndexedDB:', this.jobId);
    } catch (error) {
      // Log but don't throw - cleanup failure is non-critical
      console.warn('Failed to clean up successful recording from IndexedDB (non-critical):', error);
    }
  }

  /**
   * Flags this consultation as abandoned, from any path that ends it without a Stop.
   *
   * Takes the job id as an argument and reads no other component state, which is the entire
   * point. Its predecessor gathered `audioSegments`, `jobId`, `patientId` and the rest off
   * `this` *after* an `await`, and every caller ran `resetAll()` synchronously in the meantime
   * — so it looped over an emptied array and returned at a `!this.jobId` guard, writing
   * nothing, on every teardown there has ever been. Nothing here yields before it has what it
   * needs.
   *
   * There is no attempt to flush the recorder any more. Two reasons, both load-bearing:
   *
   *  - On `ngOnDestroy` it cannot work. `resetAll()` calls `RecordingService.cleanup()`, which
   *    destroys the recorder and nulls `onSegmentCallback`, so the final segment has nowhere
   *    to arrive even if the browser gave us the time.
   *  - On `beforeunload` it was actively harmful. Stopping the recorder there stops it whether
   *    or not the clinician then chooses "Stay" — leaving them looking at a live-looking dialog
   *    attached to a dead recorder.
   *
   * Nothing is lost by dropping it: segments are written to IndexedDB as they are cut, and the
   * parent row has existed since `startRecording()`. The most that can be missing is the
   * partial minute since the last slice, which was never recoverable on this path anyway.
   *
   * Safe to call speculatively. If the page survives — the clinician clicks "Stay", or the tab
   * comes back from the bfcache — the next slice's heartbeat write puts the status back to
   * `recording` on its own.
   */
  private async markRecordingAbandoned(jobId: string): Promise<void> {
    if (!this.recordingStorageService) return;

    try {
      await this.recordingStorageService.updateRecordingStatus(jobId, 'abandoned');
      console.log('💾 Marked in-progress recording as abandoned:', jobId);
    } catch (error) {
      // Throws when no row exists — the storage write at startRecording() failed, or this is a
      // recording that predates it. `reconcileAbandonedRecordings()` cannot help there either,
      // so there is genuinely nothing to do but say so.
      console.warn('Could not mark recording as abandoned (non-critical):', error);
    }
  }

}
